<template>
    <div class="map-container">

        <!-- ===== TOP CONTROLS ===== -->
        <div class="top-controls">

            <div class="left-controls">
                <div class="control-pill">
                    🔍 Search By location
                </div>
                <div class="control-pill">
                    29 Dec 25 - 1 Jan 26
                </div>
                <div class="control-pill">
                    📅 Week
                </div>
            </div>

            <div class="right-controls">
                <button @click="openModal" class="create-btn">
                    <v-icon left>mdi-plus</v-icon>Create Delivery Group
                </button>
            </div>

        </div>

        <!-- ===== MAP ===== -->
        <div ref="map" class="real-map"></div>

        <!-- ===== BOTTOM LEGEND ===== -->
        <div class="map-legend">
            <div class="legend-item">
                <span class="dot blue"></span> Dream Drivers
            </div>
            <div class="legend-item">
                <span class="dot orange"></span> Businesses
            </div>
            <div class="legend-item">
                <span class="dot gray"></span> Active
            </div>
            <div class="legend-item">
                <span class="dot red"></span> Allocated
            </div>
            <div class="legend-item">
                <span class="dot yellow"></span> In Progress
            </div>
            <div class="legend-item">
                <span class="dot green"></span> Completed
            </div>
        </div>

        <new-restaurant-group-modal v-if="showModal" :value="showModal" @input="showModal = $event" />
    </div>

</template>



<script>
import L from "leaflet"
import "leaflet/dist/leaflet.css"

import restaurantIconSvg from "@/assets/restaurant-marker.svg"
import waitingIconSvg from "@/assets/waiting-location-marker.svg"


export default {
    name: "RestaurantGroupMapView",

    props: {
        groups: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            showModal: false,
            map: null,
            markers: [],
            restaurantIcon: null,
            waitingIcon: null
        }
    },

    mounted() {
        this.initIcons()
        this.initMap()

        this.$nextTick(() => {
            this.renderMarkers()
        })
    },

    watch: {
        groups: {
            handler() {
                this.renderMarkers()
            },
            deep: true
        }
    },

    methods: {

        initIcons() {
            this.restaurantIcon = L.icon({
                iconUrl: restaurantIconSvg,
                iconSize: [62, 62],
                iconAnchor: [21, 42],
                popupAnchor: [0, -38]
            })

            this.waitingIcon = L.icon({
                iconUrl: waitingIconSvg,
                iconSize: [62, 62],
                iconAnchor: [25, 50],
                popupAnchor: [0, -45]
            })
        },

        initMap() {
            this.map = L.map(this.$refs.map, { zoomControl: false }).setView([51.505, -0.09], 13)

            L.control.zoom({ position: 'bottomright' }).addTo(this.map)

            L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(this.map)
        },

        clearMarkers() {
            this.markers.forEach(marker => {
                this.map.removeLayer(marker)
            })
            this.markers = []
        },

        renderMarkers() {
            if (!this.map) return

            this.clearMarkers()

            const allMarkers = []

            this.groups.forEach(group => {

                // ================= RESTAURANTS =================
                if (group.restaurants && group.restaurants.length) {

                    group.restaurants.forEach(r => {

                        if (!r.map_latitude || !r.map_longitude) return

                        const lat = Number(r.map_latitude)
                        const lng = Number(r.map_longitude)

                        if (isNaN(lat) || isNaN(lng)) return

                        const marker = L.marker(
                            [lat, lng],
                            { icon: this.restaurantIcon }
                        )
                            .addTo(this.map)
                            .bindPopup(`<strong>${r.name}</strong>`)

                        this.markers.push(marker)
                        allMarkers.push(marker)
                    })
                }

                // ================= WAITING LOCATION =================
                if (group.waiting_lat && group.waiting_lng) {

                    const lat = Number(group.waiting_lat)
                    const lng = Number(group.waiting_lng)

                    if (isNaN(lat) || isNaN(lng)) return

                    const waitingMarker = L.marker(
                        [lat, lng],
                        { icon: this.waitingIcon }
                    )
                        .addTo(this.map)
                        .bindPopup(`<strong>${group.name} - Waiting Location</strong>`)

                    this.markers.push(waitingMarker)
                    allMarkers.push(waitingMarker)
                }

            })

            // ================= AUTO FIT =================
            if (allMarkers.length) {
                const featureGroup = new L.featureGroup(allMarkers)
                this.map.fitBounds(featureGroup.getBounds().pad(0.2))
            }
        },
        openModal() { this.showModal = true },
    }
}
</script>

<style scoped>
.map-container {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 18px;
    overflow: hidden;
    background: #f4f6f8;
}

/* ================= MAP ================= */

.real-map {
    width: 100%;
    height: 100%;
    z-index: 1;
}

/* ================= TOP CONTROLS ================= */

.top-controls {
    position: absolute;
    top: 18px;
    left: 20px;
    right: 20px;
    z-index: 10;

    display: flex;
    justify-content: space-between;
    align-items: center;
}

.left-controls {
    display: flex;
    gap: 12px;
}

.control-pill {
    background: white;
    padding: 10px 18px;
    border-radius: 30px;
    font-size: 14px;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    cursor: pointer;
    transition: 0.2s ease;
}

.control-pill:hover {
    transform: translateY(-1px);
}

/* ================= CREATE BUTTON ================= */

.create-btn {
    background: #0ea5c6;
    color: white;
    border: none;
    padding: 12px 22px;
    border-radius: 30px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 6px 16px rgba(14, 165, 198, 0.4);
    transition: 0.2s ease;
}

.create-btn:hover {
    background: #0b8ea9;
}

/* ================= MAP LEGEND ================= */

.map-legend {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;

    display: flex;
    gap: 12px;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 8px;

    background: rgba(255, 255, 255, 0.95);
    padding: 10px 14px;
    border-radius: 14px;

    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;

    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
    backdrop-filter: blur(8px);

    transition: 0.2s ease;
}

.legend-item:hover {
    transform: translateY(-2px);
}


.dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

/* Colors */
.dot.blue {
    background: #2563eb;
}

.dot.orange {
    background: #f97316;
}

.dot.gray {
    background: #6b7280;
}

.dot.red {
    background: #ef4444;
}

.dot.yellow {
    background: #facc15;
}

.dot.green {
    background: #22c55e;
}
</style>
